import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { handleStripeReturn } from '../utils/paymentHandlers';

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  // Payment-specific state
  const [paymentStatus, setPaymentStatus] = useState('');
  const [paymentMessage, setPaymentMessage] = useState('');
  const [activeTransaction, setActiveTransaction] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Get auth context for user data refresh callback
  const { refreshUserData } = useAuth();

  // Handle payment status updates
  const updatePaymentStatus = (status, message) => {
    setPaymentStatus(status);
    setPaymentMessage(message);
  };

  // Handle successful payment completion
  const onPaymentSuccess = async () => {
    try {
      // Refresh user data from AuthContext (maintains existing integration)
      await refreshUserData();
      
      // Set success status
      updatePaymentStatus('success', 'Payment completed successfully! Your account has been updated.');
      
      // Clear active transaction
      setActiveTransaction(null);
      setIsProcessingPayment(false);
    } catch (error) {
      console.error('Error refreshing user data after payment:', error);
      updatePaymentStatus('error', 'Payment completed but failed to refresh account data. Please refresh the page.');
    }
  };

  // Handle payment failure
  const onPaymentError = (message = 'Payment failed. Please try again.') => {
    updatePaymentStatus('error', message);
    setActiveTransaction(null);
    setIsProcessingPayment(false);
  };

  // Handle payment cancellation
  const onPaymentCancelled = () => {
    updatePaymentStatus('info', 'Payment was cancelled. You can try again anytime.');
    setActiveTransaction(null);
    setIsProcessingPayment(false);
  };

  // Process Stripe return (extracted from AuthContext)
  const processStripeReturn = async () => {
    try {
      setIsProcessingPayment(true);
      
      const handled = await handleStripeReturn(
        updatePaymentStatus,
        onPaymentSuccess
      );
      
      if (handled) {
        // Clean up URL after processing
        setTimeout(() => {
          window.history.replaceState({}, document.title, window.location.pathname);
        }, 1000);
        
        // Clear messages after delay
        setTimeout(() => {
          clearPaymentStatus();
        }, 5000);
      } else {
        setIsProcessingPayment(false);
      }
      
      return handled;
    } catch (error) {
      console.error('Error processing Stripe return:', error);
      onPaymentError('Failed to process payment verification. Please contact support.');
      return false;
    }
  };

  // Clear payment status messages
  const clearPaymentStatus = () => {
    setPaymentStatus('');
    setPaymentMessage('');
  };

  // Start a new payment transaction
  const startPaymentTransaction = (transactionData) => {
    setActiveTransaction(transactionData);
    setIsProcessingPayment(true);
    clearPaymentStatus();
  };

  const value = {
    // Payment state
    paymentStatus,
    paymentMessage,
    activeTransaction,
    isProcessingPayment,
    
    // Payment actions
    updatePaymentStatus,
    onPaymentSuccess,
    onPaymentError,
    onPaymentCancelled,
    processStripeReturn,
    clearPaymentStatus,
    startPaymentTransaction
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
};